import {
    CollectionPaymentSourceType,
    createRequestFilters,
    FilterMethod,
    GetProviderCollectionDraftInvoicesRequest,
    GetProviderCollectionInvoicesRequest,
    InvoiceDraftApproveActionRequest,
    InvoiceDraftCreateRequest,
    InvoiceDraftDenyActionRequest,
    ProviderAllocateCollectionPaymentSourceListPayload,
    ProviderCollectionInvoice,
    ProviderCollectionInvoiceDraft,
    ProviderCollectionInvoiceDraftPayment,
    ProviderCollectionInvoiceDraftPreview,
    ProviderCollectionInvoiceIntegrationItem,
    ProviderCollectionInvoiceSummaryItem,
    ProviderCollectionPayment,
    ProviderCollectionPaymentSourceWithAllocationInfo,
} from '@enter/core';
import _ from 'lodash';
import { action, makeObservable, observable, runInAction } from 'mobx';
import type { CollectionInvoiceGenerator } from './collection-invoice.generator';
import { CollectionInvoiceService } from './collection-invoice.service';
import { IFeatures, IItemListing, IItemListingRequest } from './root.store';
import { appendTo, parseItemListingRequest } from './util';

export type PatientCollectionsTab = 'needs' | 'ready' | 'outstanding' | 'paid';

/** Real collection payment source shown in Resolve — never synthesized from line totals. */
export type CollectionAccountPayment = {
    id: string;
    method: string;
    date: string;
    amount: number;
    unallocatedAmount: number;
    allocatedToThisClaim: boolean;
    /** Full source amount from the payments API — used for Total / elsewhere in Manage allocation. */
    sourceTotal: number;
};

export class CollectionInvoiceStore {
    @observable public integrations: ProviderCollectionInvoiceIntegrationItem[] = [];
    @observable public selectedIntegrationId: string | undefined;
    @observable public summary: ProviderCollectionInvoiceSummaryItem | undefined;
    @observable public drafts: ProviderCollectionInvoiceDraft[] = [];
    @observable public invoices: ProviderCollectionInvoice[] = [];
    @observable public summaryLoading = false;
    @observable public integrationsLoaded = false;

    private service: CollectionInvoiceService = new CollectionInvoiceService();
    private integrationsPromise: Promise<ProviderCollectionInvoiceIntegrationItem[]> | undefined;
    /** Patient filter the current summary was requested with — guards stale writes (see loadSummary). */
    private summaryExternalPatientId: string | undefined;
    private features: IFeatures;
    /**
     * Serves this screen in demo mode. There used to be no demo branch here at all, so every call
     * went to the real service, the demo boundary refused it, and the page opened on "A system
     * exception has occurred" over "Not configured" (sales, 2026-09-10). A proxy until demo mode
     * loads the generators — touched only inside a demo branch.
     */
    private collectionInvoiceGenerator: CollectionInvoiceGenerator;

    constructor(features: IFeatures, collectionInvoiceGenerator: CollectionInvoiceGenerator) {
        this.features = features;
        this.collectionInvoiceGenerator = collectionInvoiceGenerator;
        makeObservable(this);
    }

    public findDraft = (id: string): ProviderCollectionInvoiceDraft | undefined =>
        _.find(this.drafts, { collectionInvoiceDraftId: id });

    /** Next open Needs Input draft after `currentId` (wraps to the first remaining). */
    public nextOpenDraftId = (currentId: string): string | undefined => {
        const open = this.drafts.filter(
            d => d.collectionInvoiceDraftId && !d.approveDate && !d.denyDate,
        );
        const remaining = open.filter(d => d.collectionInvoiceDraftId !== currentId);
        if (remaining.length === 0) {
            return undefined;
        }
        const i = open.findIndex(d => d.collectionInvoiceDraftId === currentId);
        const after = i >= 0 ? open.slice(i + 1).filter(d => d.collectionInvoiceDraftId !== currentId) : [];
        return (after[0] || remaining[0]).collectionInvoiceDraftId;
    };

    public findInvoice = (id: string): ProviderCollectionInvoice | undefined =>
        _.find(this.invoices, { collectionInvoiceId: id });

    /**
     * Account-level patient payments for the Resolve dialog.
     * Backing rows come from draft / preview `payments` (collectionPaymentSourceId),
     * never from line-item `patientPayments` (NS-4). Unallocated sources are listed
     * so they can be allocated onto this claim.
     */
    public async loadAccountPayments(
        draft: ProviderCollectionInvoiceDraft,
        previewPayments?: ProviderCollectionInvoiceDraftPayment[],
    ): Promise<CollectionAccountPayment[]> {
        const backing =
            previewPayments && previewPayments.length > 0 ? previewPayments : draft.payments || [];
        const externalPatientId = draft.externalPatientId;
        const claimId = draft.claimId;

        const sourcesRequest = {
            filterExternalPatientId: [`${FilterMethod.EQ}:${externalPatientId}`],
            limit: 100,
            offset: 0,
        };
        const paymentsRequest = {
            filterClaimId: [`${FilterMethod.EQ}:${claimId}`],
            limit: 100,
            offset: 0,
        };
        const [sources, claimPayments] = await Promise.all([
            externalPatientId
                ? this.features.isDemo
                    ? this.collectionInvoiceGenerator.loadPaymentSources(sourcesRequest)
                    : this.service.fetchPaymentSources(sourcesRequest)
                : Promise.resolve([] as ProviderCollectionPaymentSourceWithAllocationInfo[]),
            claimId
                ? this.features.isDemo
                    ? this.collectionInvoiceGenerator.loadPayments(paymentsRequest)
                    : this.service.fetchPayments(paymentsRequest)
                : Promise.resolve([] as ProviderCollectionPayment[]),
        ]);

        return mergeAccountPayments(backing, sources, claimPayments);
    }

    public allocatePaymentSource(
        collectionPaymentSourceId: string,
        payload: ProviderAllocateCollectionPaymentSourceListPayload,
    ): Promise<ProviderCollectionPaymentSourceWithAllocationInfo> {
        return this.features.isDemo
            ? this.collectionInvoiceGenerator.allocatePaymentSource(collectionPaymentSourceId, payload)
            : this.service.allocatePaymentSource(collectionPaymentSourceId, payload);
    }

    @action
    public setSelectedIntegrationId(id: string): void {
        this.selectedIntegrationId = id;
    }

    /** Switch integration and drop cached rows so tables refetch for the new id. */
    @action
    public selectIntegration(id: string): void {
        if (this.selectedIntegrationId === id) {
            return;
        }
        this.selectedIntegrationId = id;
        this.drafts = [];
        this.invoices = [];
        this.summary = undefined;
        this.summaryLoading = true;
    }

    @action
    public async loadIntegrations(): Promise<ProviderCollectionInvoiceIntegrationItem[]> {
        if (this.integrationsLoaded) {
            return this.integrations;
        }
        if (this.integrationsPromise) {
            return this.integrationsPromise;
        }
        this.integrationsPromise = (async () => {
            try {
                const items =
                    (await (this.features.isDemo
                        ? this.collectionInvoiceGenerator.loadIntegrations()
                        : this.service.fetchIntegrations())) || [];
                runInAction(() => {
                    this.integrations = items;
                    this.integrationsLoaded = true;
                    if (!this.selectedIntegrationId && this.integrations.length) {
                        this.selectedIntegrationId = this.integrations[0].id;
                    }
                });
                return this.integrations;
            } finally {
                this.integrationsPromise = undefined;
            }
        })();
        return this.integrationsPromise;
    }

    /**
     * KPI summary for the selected integration, narrowed to one patient when `externalPatientId`
     * is set (the page's patient search). The requested filter is remembered so a response that
     * raced a newer search cannot clobber the newer one — the same trick the integration id
     * guard plays.
     */
    @action
    public async loadSummary(
        integrationId?: string,
        externalPatientId?: string,
    ): Promise<ProviderCollectionInvoiceSummaryItem> {
        const id = integrationId || this.selectedIntegrationId;
        if (!id) {
            throw new Error('Collection invoice integration is required');
        }
        const patient = externalPatientId?.trim() || undefined;
        // Scope change: drop stale KPIs (mirrors selectIntegration) so the cards render as
        // loading instead of the previous scope's numbers.
        if (this.summaryExternalPatientId !== patient) {
            this.summary = undefined;
        }
        this.summaryExternalPatientId = patient;
        this.summaryLoading = true;
        try {
            const summary = await (this.features.isDemo
                ? this.collectionInvoiceGenerator.loadSummary({
                      collectionInvoiceIntegrationId: id,
                      externalPatientId: patient,
                  })
                : this.service.fetchSummary({ collectionInvoiceIntegrationId: id, externalPatientId: patient }));
            runInAction(() => {
                if (this.selectedIntegrationId === id && this.summaryExternalPatientId === patient) {
                    this.summary = summary;
                }
            });
            return summary;
        } finally {
            runInAction(() => {
                if (this.selectedIntegrationId === id && this.summaryExternalPatientId === patient) {
                    this.summaryLoading = false;
                }
            });
        }
    }

    @action
    public async loadDraftListing(
        ilr: IItemListingRequest,
        integrationId?: string,
    ): Promise<IItemListing<ProviderCollectionInvoiceDraft>> {
        const selectedId = integrationId || this.selectedIntegrationId;
        if (!selectedId) {
            throw new Error('Collection invoice integration is required');
        }
        const req = ilr2DraftRequest(ilr, selectedId);
        const listing = await (this.features.isDemo
            ? this.collectionInvoiceGenerator.loadDraftListing(req)
            : this.service.fetchDraftInvoices(req));
        if (!listing) {
            throw new Error('No draft invoices available');
        }
        runInAction(() => {
            this.drafts = appendTo(this.drafts, listing.items, 'collectionInvoiceDraftId');
        });
        return listing;
    }

    @action
    public async loadInvoiceListing(
        ilr: IItemListingRequest,
        tab: Exclude<PatientCollectionsTab, 'needs'>,
        integrationId?: string,
    ): Promise<IItemListing<ProviderCollectionInvoice>> {
        const selectedId = integrationId || this.selectedIntegrationId;
        if (!selectedId) {
            throw new Error('Collection invoice integration is required');
        }
        const req = ilr2InvoiceRequest(ilr, tab, selectedId);
        const listing = await (this.features.isDemo
            ? this.collectionInvoiceGenerator.loadInvoiceListing(req, tab)
            : this.service.fetchInvoices(req));
        if (!listing) {
            throw new Error('No collection invoices available');
        }
        runInAction(() => {
            this.invoices = appendTo(this.invoices, listing.items, 'collectionInvoiceId');
        });
        return listing;
    }

    @action
    public async loadInvoiceById(
        collectionInvoiceId: string,
        integrationId?: string,
    ): Promise<ProviderCollectionInvoice | undefined> {
        const cached = this.findInvoice(collectionInvoiceId);
        if (cached) {
            return cached;
        }

        const id = integrationId || this.selectedIntegrationId;
        if (!id) {
            throw new Error('Collection invoice integration is required');
        }
        const byIdRequest = {
            filterCollectionInvoiceIntegrationId: [`${FilterMethod.EQ}:${id}`],
            filterRetractDate: [FilterMethod.EMPTY],
            filterDeclineDate: [FilterMethod.EMPTY],
            limit: 100,
            offset: 0,
        };
        const res = await (this.features.isDemo
            ? this.collectionInvoiceGenerator.loadInvoiceListing(byIdRequest)
            : this.service.fetchInvoices(byIdRequest));
        const match = res?.items.find(i => i.collectionInvoiceId === collectionInvoiceId);
        if (match) {
            runInAction(() => {
                this.invoices = appendTo(this.invoices, [match], 'collectionInvoiceId');
            });
            return match;
        }
        return undefined;
    }

    @action
    public async loadDraftById(
        collectionInvoiceDraftId: string,
        integrationId?: string,
    ): Promise<ProviderCollectionInvoiceDraft | undefined> {
        const cached = this.findDraft(collectionInvoiceDraftId);
        if (cached) {
            return cached;
        }
        const listing = await this.loadDraftListing(
            { page: 0, pageSize: 100, filters: [] },
            integrationId,
        );
        return listing.items.find(d => d.collectionInvoiceDraftId === collectionInvoiceDraftId);
    }

    public previewDraftInvoice(
        invoiceDraftCreateRequest: InvoiceDraftCreateRequest,
    ): Promise<ProviderCollectionInvoiceDraftPreview> {
        return this.features.isDemo
            ? this.collectionInvoiceGenerator.previewDraftInvoice(invoiceDraftCreateRequest)
            : this.service.previewDraftInvoice(invoiceDraftCreateRequest);
    }

    public createDraftInvoice(
        invoiceDraftCreateRequest: InvoiceDraftCreateRequest,
    ): Promise<ProviderCollectionInvoiceDraft> {
        return this.features.isDemo
            ? this.collectionInvoiceGenerator.createDraftInvoice(invoiceDraftCreateRequest)
            : this.service.createDraftInvoice(invoiceDraftCreateRequest);
    }

    public async approveDraftInvoice(
        id: string,
        invoiceDraftApproveActionRequest: InvoiceDraftApproveActionRequest,
    ): Promise<ProviderCollectionInvoice> {
        if (!this.features.isDemo) {
            return this.service.approveDraftInvoice(id, invoiceDraftApproveActionRequest);
        }
        const invoice = await this.collectionInvoiceGenerator.approveDraftInvoice(id, invoiceDraftApproveActionRequest);
        this.markDemoDraftResolved(id, { approveDate: invoice.approveDate });
        return invoice;
    }

    public async denyDraftInvoice(
        id: string,
        invoiceDraftDenyActionRequest: InvoiceDraftDenyActionRequest,
    ): Promise<ProviderCollectionInvoiceDraft> {
        if (!this.features.isDemo) {
            return this.service.denyDraftInvoice(id, invoiceDraftDenyActionRequest);
        }
        const draft = await this.collectionInvoiceGenerator.denyDraftInvoice(id, invoiceDraftDenyActionRequest);
        this.markDemoDraftResolved(id, { denyDate: draft.denyDate });
        return draft;
    }

    /**
     * Demo only. `drafts` is a merge cache the listing never prunes, so a draft resolved in demo
     * mode still read as open, and `nextOpenDraftId` could offer it again after a later confirmation
     * — reopening Resolve on a balance that was already queued.
     */
    @action
    private markDemoDraftResolved(
        id: string,
        resolution: Pick<ProviderCollectionInvoiceDraft, 'approveDate' | 'denyDate'>,
    ): void {
        this.drafts = this.drafts.map(draft =>
            draft.collectionInvoiceDraftId === id ? { ...draft, ...resolution } : draft,
        );
    }
}

const withIntegrationFilter = <T extends { filterCollectionInvoiceIntegrationId?: Array<string> }>(
    req: T,
    integrationId?: string,
): T => {
    if (!integrationId) {
        return req;
    }
    return {
        ...req,
        filterCollectionInvoiceIntegrationId: [`${FilterMethod.EQ}:${integrationId}`],
    };
};

export const ilr2DraftRequest = (
    ilr: IItemListingRequest,
    integrationId?: string,
): GetProviderCollectionDraftInvoicesRequest => {
    const base = parseItemListingRequest(ilr) as GetProviderCollectionDraftInvoicesRequest;
    const filters = createRequestFilters(_.get(ilr, 'filters'), true);
    // Needs Input: open drafts only
    const tabFilters: Partial<GetProviderCollectionDraftInvoicesRequest> = {
        filterApproveDate: [FilterMethod.EMPTY],
        filterDenyDate: [FilterMethod.EMPTY],
    };
    return withIntegrationFilter(
        {
            ...base,
            ...filters,
            ...tabFilters,
        },
        integrationId,
    );
};

export const ilr2InvoiceRequest = (
    ilr: IItemListingRequest,
    tab: Exclude<PatientCollectionsTab, 'needs'>,
    integrationId?: string,
): GetProviderCollectionInvoicesRequest => {
    const base = parseItemListingRequest(ilr) as GetProviderCollectionInvoicesRequest;
    const filters = createRequestFilters(_.get(ilr, 'filters'), true);
    // Shared: exclude retracted / declined
    const tabFilters: Partial<GetProviderCollectionInvoicesRequest> = {
        filterRetractDate: [FilterMethod.EMPTY],
        filterDeclineDate: [FilterMethod.EMPTY],
    };

    if (tab === 'ready') {
        // Ready to Send: not yet posted
        tabFilters.filterPostDate = [FilterMethod.EMPTY];
    } else if (tab === 'outstanding') {
        // Outstanding: posted, non-zero balance
        tabFilters.filterPostDate = [FilterMethod.NEMPTY];
        tabFilters.filterCurrentBalance = [`${FilterMethod.NQ}:0`];
    } else if (tab === 'paid') {
        // Paid: zero balance
        tabFilters.filterCurrentBalance = [`${FilterMethod.EQ}:0`];
    }

    return withIntegrationFilter(
        {
            ...base,
            ...filters,
            ...tabFilters,
        },
        integrationId,
    );
};

const amountNum = (a?: { amount?: string }): number => {
    const n = Number(a?.amount || 0);
    return Number.isFinite(n) ? n : 0;
};

const paymentSourceLabel = (source?: CollectionPaymentSourceType | string): string => {
    switch (source) {
        case CollectionPaymentSourceType.CARD:
            return 'Card';
        case CollectionPaymentSourceType.BANKACCOUNT:
            return 'Bank account';
        case CollectionPaymentSourceType.CHECK:
            return 'Check';
        case CollectionPaymentSourceType.PREPAYMENT:
            return 'Prepayment';
        case CollectionPaymentSourceType.OFFICEPAYMENT:
            return 'Office payment';
        case CollectionPaymentSourceType.EXTERNALINBOX:
            return 'Inbox payment';
        case CollectionPaymentSourceType.EXTERNALTHIRDPARTY:
            return 'Third-party payment';
        case CollectionPaymentSourceType.TERMINAL:
            return 'Terminal';
        case CollectionPaymentSourceType.OTHER:
            return 'Other';
        default:
            return source ? String(source).replace(/_/g, ' ') : 'Patient payment';
    }
};

const mergeAccountPayments = (
    draftPayments: ProviderCollectionInvoiceDraftPayment[],
    sources: ProviderCollectionPaymentSourceWithAllocationInfo[],
    claimPayments: ProviderCollectionPayment[],
): CollectionAccountPayment[] => {
    const claimBySourceId = new Map<string, ProviderCollectionPayment>();
    claimPayments.forEach(p => {
        if (p.collectionPaymentSourceId) {
            claimBySourceId.set(p.collectionPaymentSourceId, p);
        }
    });
    draftPayments.forEach(p => {
        if (p.collectionPaymentSourceId && !claimBySourceId.has(p.collectionPaymentSourceId)) {
            claimBySourceId.set(p.collectionPaymentSourceId, {
                id: p.collectionPaymentId,
                collectionPaymentSourceId: p.collectionPaymentSourceId,
                collectedAmount: p.paymentAmount,
                paymentDate: p.paymentDate,
            });
        }
    });

    const rows = new Map<string, CollectionAccountPayment>();

    sources.forEach(source => {
        const id = source.id;
        if (!id) {
            return;
        }
        const claimPmt = claimBySourceId.get(id);
        const allocatedToThisClaim = !!claimPmt;
        const unallocatedAmount = amountNum(source.unallocatedAmount);
        const sourceTotal = amountNum(source.amount);
        if (!allocatedToThisClaim && unallocatedAmount <= 0) {
            return;
        }
        rows.set(id, {
            id,
            method: paymentSourceLabel(source.source),
            date: source.paymentDate || source.created || claimPmt?.paymentDate || '',
            amount: allocatedToThisClaim ? amountNum(claimPmt?.collectedAmount) : sourceTotal,
            unallocatedAmount,
            allocatedToThisClaim,
            sourceTotal,
        });
    });

    claimBySourceId.forEach((claimPmt, sourceId) => {
        if (rows.has(sourceId)) {
            return;
        }
        rows.set(sourceId, {
            id: sourceId,
            method: paymentSourceLabel(claimPmt.source),
            date: claimPmt.paymentDate || claimPmt.created || '',
            amount: amountNum(claimPmt.collectedAmount),
            unallocatedAmount: 0,
            allocatedToThisClaim: true,
            sourceTotal: amountNum(claimPmt.collectedAmount),
        });
    });

    return Array.from(rows.values());
};
